import moment from 'moment-timezone';
import React from 'react';
import { ORDER_STATUS } from 'src/constants/common';
import { getDateWithSuffixFormat, getFormattedDate } from 'src/utils/CommonFunctions';

/**
 * Single source of the Radar empty-state copy for BOTH the map sidebar and the table view.
 *
 * Mirrors WAL's `makeCustomTitleDesc` (wal-frontend-v2 Radar/MapTableViewNew.tsx) exactly —
 * same i18n keys, same date suffix, same wording. Previously each view built its own strings,
 * which is how the table view drifted onto copy ("No pickups found") that exists nowhere in WAL.
 * Keep this the ONLY place the copy lives so the two views cannot diverge again.
 *
 * Titles are ReactNode, not string: the date carries a <sup> suffix ("Sep 21<sup>st</sup>, 2026")
 * that has to render as HTML. It is derived from a moment format, never from user input.
 */
export const makeTrackEmptyStateTitleDesc = (
    params: { status?: string; startDate?: string | null; endDate?: string | null },
    isSearchActive: boolean,
    userTimezone: string,
    t: (key: string, opts?: any) => string
): { title: React.ReactNode; desc: React.ReactNode } => {
    // A single-day range names that day ("Today" / "Yesterday" / "Tomorrow" / "on Sep 21st, 2026");
    // a multi-day range has no single day to name, so WAL falls back to today.
    let currentTimeRange = (
        moment(params?.startDate).format('DD-MM-YYYY') === moment(params?.endDate).format('DD-MM-YYYY')
            ? getDateWithSuffixFormat(getFormattedDate(params?.startDate, 'MMM Do, YYYY'))
            : getDateWithSuffixFormat(getFormattedDate(moment().tz(userTimezone).format('YYYY-MM-DD'), 'MMM Do, YYYY'))
    )?.trim();

    // "Today" reads on its own; a calendar date needs the preposition ("on Sep 21st, 2026").
    if (currentTimeRange && currentTimeRange !== 'Today' && currentTimeRange !== 'Yesterday' && currentTimeRange !== 'Tomorrow') {
        currentTimeRange = `on ${currentTimeRange}`;
    }

    if (isSearchActive) {
        return {
            title: <span>{t('Radar.No_orders_match_your_search_criteria')}</span>,
            desc: <span>{t('Radar.Clear_filters_or_change_your_criteria_to_see_available_results')}</span>,
        };
    }

    switch (params?.status) {
        case ORDER_STATUS.DISPATCHED:
            return {
                title: (
                    <span>
                        {t('Radar.Nothing_Scheduled_for')} {t('Radar.Pick_Up')} <span dangerouslySetInnerHTML={{ __html: currentTimeRange }} />
                    </span>
                ),
                desc: <span>{t('Radar.You_re_all_caught_up_Schedule_a_new', { isStatusWiseDesc: t('Radar.pickup') })}</span>,
            };

        case ORDER_STATUS.IN_TRANSIT:
            return {
                title: <span>{t('Radar.Nothing_Currently_In_Transit')}</span>,
                desc: <span>{t('Radar.No_shipments_are_currently_in_In_Transit')}</span>,
            };

        case ORDER_STATUS.DELIVERED:
            return {
                title: (
                    <span>
                        {t('Radar.Nothing_Scheduled_for_Delivery')} <span dangerouslySetInnerHTML={{ __html: currentTimeRange }} />
                    </span>
                ),
                desc: <span dangerouslySetInnerHTML={{ __html: t('Radar.There_are_no_deliveries__Please_check_back_later') }} />,
            };

        default:
            return {
                title: <span>{t('Radar.No_Results_with_Current_Filters')}</span>,
                desc: <span>{t('Radar.Clear_filters_or_change_your_criteria_to_see_available_results')}</span>,
            };
    }
};
